import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  IconBellRinging,
  IconSearch,
  IconFilter,
  IconRefresh,
  IconChevronUp,
  IconChevronDown,
  IconCirclePlus,
  IconPencil,
  IconTrash,
  IconX,
  IconLock,
  IconLockOpen,
  IconSwitchHorizontal
} from "@tabler/icons-react";
import Logo from "../../assets/images/paqtory.png";
import AdminLayout from "../../components/common/AdminLayout";

// Redux
import { useDispatch, useSelector } from "react-redux";
import {
  fetchUsers,
  searchAndFilterCustomerAccount,
  toggleUserAccountsStatus,
  deleteCustomerAccounts
} from "../../redux/slices/usersSlice";

import Sidebar from "../../components/common/AdminSideBar";
import Header from "../../components/common/AdminHeader";
import { useAdminLayout } from "../../context/adminLayoutUseContext";
import LoadingFallback from "../../components/common/LoadingFallback";
import ErrorFallback from "../../components/common/ErrorFallback";

/* =========================================
   NavItem for Sidebar Links 
========================================= */
function NavItem({ label, to }) {
  return (
    <a
      href={to}
      className="px-3 py-2 rounded-md cursor-pointer flex items-center gap-2 hover:bg-white/10"
    >
      <span>{label}</span>
    </a>
  );
}

/* =========================================
   IconButton: Reusable for small icon buttons
========================================= */
function IconButton({ children, onClick }) {
  return (
    <button onClick={onClick} className="p-1 hover:bg-gray-100 rounded transition">
      {children}
    </button>
  );
}

/* =========================================
   MODALS: Add, Edit, Toggle, Delete
========================================= */

// ADD USER
function AddUserModal({ onClose }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [status, setStatus] = useState("Active");
  const [role, setRole] = useState("User");

  function handleSave() {
    onClose();
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white w-[600px] max-w-full rounded-md p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
        >
          <IconX size={20} />
        </button>
        <h2 className="text-lg font-semibold text-[#0B0C52] mb-4">
          Add New User
        </h2>

        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-semibold mb-1">Name</label>
            <input
              type="text"
              placeholder="Enter name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="border-2 border-[#463BC9] w-full rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#463BC9]"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold mb-1">Email</label>
            <input
              type="email"
              placeholder="Enter email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="border-2 border-[#463BC9] w-full rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#463BC9]"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold mb-1">Phone</label>
            <input
              type="text"
              placeholder="Enter phone number"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="border-2 border-[#463BC9] w-full rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#463BC9]"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold mb-1">Status</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="border-2 border-[#463BC9] w-full rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#463BC9]"
            >
              <option>Active</option>
              <option>Inactive</option>
              <option>Banned</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold mb-1">Role</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="border-2 border-[#463BC9] w-full rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#463BC9]"
            >
              <option>User</option>
              <option>Admin</option>
            </select>
          </div>
        </div>

        <div className="flex gap-4">
          <button
            onClick={handleSave}
            className="bg-[#0B0C52] text-white px-4 py-2 rounded hover:opacity-90"
          >
            Save
          </button>
          <button
            onClick={onClose}
            className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:opacity-80"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// EDIT USER
function EditUserModal({ user, onClose }) {
  const [name, setName] = useState(user.name);
  const [email, setEmail] = useState(user.email);
  const [phone, setPhone] = useState(user.phone);
  const [status, setStatus] = useState(user.status);
  const [role, setRole] = useState(user.role);

  function handleUpdate() {
    onClose();
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white w-[600px] max-w-full rounded-md p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
        >
          <IconX size={20} />
        </button>
        <h2 className="text-lg font-semibold text-[#0B0C52] mb-4">
          Edit User
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          Editing user: <strong>{user.name}</strong>
        </p>

        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-semibold mb-1">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="border-2 border-[#463BC9] w-full rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#463BC9]"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold mb-1">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="border-2 border-[#463BC9] w-full rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#463BC9]"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold mb-1">Phone</label>
            <input
              type="text"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="border-2 border-[#463BC9] w-full rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#463BC9]"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold mb-1">Status</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="border-2 border-[#463BC9] w-full rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#463BC9]"
            >
              <option>Active</option>
              <option>Inactive</option>
              <option>Banned</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold mb-1">Role</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="border-2 border-[#463BC9] w-full rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#463BC9]"
            >
              <option>User</option>
              <option>Admin</option>
            </select>
          </div>
        </div>

        <div className="flex gap-4">
          <button
            onClick={handleUpdate}
            className="bg-[#0B0C52] text-white px-4 py-2 rounded hover:opacity-90"
          >
            Update
          </button>
          <button
            onClick={onClose}
            className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:opacity-80"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// TOGGLE USER MODAL (single or batch)
function ToggleUserModal({ user, onClose, onConfirm }) {
  const isBatch = !user.full_name;
  const isActive = user.account_status?.toString().trim().toLowerCase() === "active";
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white w-[500px] max-w-full rounded-md p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
        >
          <IconX size={20} />
        </button>
        <h2 className="text-lg font-semibold text-yellow-600 mb-4 flex items-center gap-2">
          <IconSwitchHorizontal size={20} />
          {isBatch ? "Toggle User Status" : (isActive ? "Deactivate User" : "Activate User")}
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          {isBatch
            ? "Are you sure you want to toggle the status of the selected users?"
            : (
              isActive
                ? <>Are you sure you want to <strong>deactivate</strong> <strong>{user.full_name}</strong>?<br />This user will no longer be able to access certain features.</>
                : <>Are you sure you want to <strong>activate</strong> <strong>{user.full_name}</strong>?<br />This user will regain access to all features.</>
            )
          }
        </p>
        <div className="flex gap-4">
          <button
            onClick={() => { if (onConfirm) onConfirm(); onClose(); }}
            className="bg-yellow-600 text-white px-4 py-2 rounded hover:opacity-90"
          >
            Confirm
          </button>
          <button
            onClick={onClose}
            className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:opacity-80"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// DELETE USER
function DeleteUserModal({ user, onClose, onConfirm }) {
  function handleDelete() {
    if (onConfirm) onConfirm();
    onClose();
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white w-[500px] max-w-full rounded-md p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
        >
          <IconX size={20} />
        </button>
        <h2 className="text-lg font-semibold text-red-600 mb-4 flex items-center gap-2">
          <IconTrash size={20} />
          Delete User
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          Are you sure you want to permanently delete{" "}
          <strong>{user.name}</strong>?<br />
          This action cannot be undone.
        </p>
        <div className="flex gap-4">
          <button
            onClick={handleDelete}
            className="bg-red-500 text-white px-4 py-2 rounded hover:opacity-90"
          >
            Delete
          </button>
          <button
            onClick={onClose}
            className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:opacity-80"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// FILTER MODAL
function FilterModal({
  isOpen,
  onClose,
  orderHistory,
  setOrderHistory,
  status,
  setStatus,
  onApply
}) {
  if (!isOpen) return null;

  const handleReset = () => {
    setOrderHistory("");
    setStatus("");
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white w-[400px] max-w-full rounded-md p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
        >
          <IconX size={20} />
        </button>
        <h2 className="text-lg font-semibold text-[#0B0C52] mb-4">Filter Options</h2>

        <div className="mb-4">
          <label className="block text-sm font-semibold mb-1">Order History</label>
          <select
            value={orderHistory}
            onChange={(e) => setOrderHistory(e.target.value)}
            className="border border-gray-300 rounded px-3 py-2 w-full"
          >
            <option value="">Select Order History</option>
            <option value="no_orders">No Orders</option>
            <option value="1-5">1-5 Orders</option>
            <option value="6-10">6-10 Orders</option>
            <option value="11-25">11-25 Orders</option>
            <option value="26-50">26-50 Orders</option>
            <option value="50+">50+ Orders</option>
          </select>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-semibold mb-1">Status</label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="border border-gray-300 rounded px-3 py-2 w-full"
          >
            <option value="">Select Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="banned">Banned</option>
          </select>
        </div>

        <div className="flex justify-end gap-4">
          <button
            onClick={handleReset}
            className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:opacity-80"
          >
            Reset
          </button>
          <button
            onClick={onClose}
            className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:opacity-80"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              onApply();
              onClose();
            }}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          >
            Apply Filters
          </button>
        </div>
      </div>
    </div>
  );
}

/* =========================================
   Main Component: UserAccountManagement
========================================= */
function UserAccountManagement() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { setLayoutConfig } = useAdminLayout();

  const { loading, isSearching, error, data } = useSelector((state) => state.users);

  // Local state
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [modalType, setModalType] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [orderHistory, setOrderHistory] = useState("");
  const [status, setStatus] = useState("");
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [modalActionHandler, setModalActionHandler] = useState(null);

  const pageSize = 10;

  useEffect(() => {
    setLayoutConfig({
      title: "User Accounts",
      showBackButton: false,
      scrollable: false,
    });
  }, [setLayoutConfig]);

  useEffect(() => {
    if (searchTerm.trim() !== '' || orderHistory || status) {
      dispatch(searchAndFilterCustomerAccount({ searchTerm, orderHistory, accountStatus: status || 'active' }));
    } else {
      dispatch(fetchUsers());
    }
  }, [searchTerm, orderHistory, status, dispatch]);

  const { list_of_users, customer_accounts } = data || { list_of_users: [], customer_accounts: {} };

  const filteredUsers = list_of_users.filter((user) => {
    const inName = user.full_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const inEmail = user.email?.toLowerCase().includes(searchTerm.toLowerCase());
    return inName || inEmail;
  });

  const totalCount = filteredUsers.length;
  const totalPages = Math.ceil(totalCount / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentUsers = filteredUsers.slice(startIndex, endIndex);
  const fromCount = startIndex + 1;
  const toCount = endIndex > totalCount ? totalCount : endIndex;

  // Batch Delete Handler
  const handleBatchDelete = () => {
    if (selectedUsers.length === 0) return;
    openModal("DELETE", { name: "selected users" }, () => {
      dispatch(deleteCustomerAccounts(selectedUsers));
      setSelectedUsers([]);
    });
  };

  // Batch Status Toggle Handler
  const handleBatchToggleStatus = () => {
    if (selectedUsers.length === 0) return;
    openModal("TOGGLE", { name: "selected users" }, async () => {
      try {
        await dispatch(toggleUserAccountsStatus(selectedUsers)).unwrap();
        // The Redux reducer now handles the UI update, so we don't need to re-fetch
        setSelectedUsers([]);
      } catch (err) {
        console.error("Error toggling user statuses:", err);
      }
    });
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  function openModal(type, user = null, actionHandler = null) {
    setSelectedUser(user);
    setModalType(type);
    setModalActionHandler(() => actionHandler);
  }

  function closeModal() {
    setSelectedUser(null);
    setModalType(null);
  }

  const handleFilter = () => {
    dispatch(
      searchAndFilterCustomerAccount({
        searchTerm,
        orderHistory,
        accountStatus: status || "active",
      })
    );
    setCurrentPage(1);
  };

  // Selection handlers
  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const selectAllUsers = () => {
    if (selectedUsers.length === currentUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(currentUsers.map(user => user.id));
    }
  };

  if (loading) {
    return <LoadingFallback />;
  }

  if (error) {
    return <ErrorFallback error={error} onRetry={() => dispatch(fetchUsers())} />;
  }

  return (
    <div>
      <main className=" flex-1 bg-[#FFFFFF] text-gray-800 px-4  rounded">
        <div className="  rounded-md bg-[#FFFFFF] py-4">
          {/* TOOLBAR */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            {selectedUsers.length > 0 ? (
              <div className="flex flex-wrap items-center justify-between gap-2 mb-4 w-full">
                {/* Left side - selected count and search */}
                <div className="flex items-center gap-4">
                  <span className="text-sm text-gray-700 whitespace-nowrap">
                    {selectedUsers.length} selected
                  </span>
                  <div className="relative ml-1">
                    <input
                      type="text"
                      placeholder="Search By Name Or Email"
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                      }}
                      className="
                        border-2 border-[#463BC9] rounded text-sm px-2 py-1 pl-8 
                        focus:outline-none focus:border-[3px] 
                        peer
                      "
                    />
                    <IconSearch
                      size={16}
                      className="absolute left-2 top-2 text-gray-400 peer-focus:text-[#463BC9]"
                    />
                  </div>
                </div>
                {/* Right side - batch action buttons */}
                <div className="flex items-center gap-2">
                  <button
                    className="text-red-500 hover:text-red-700 flex items-center gap-1 text-sm px-3 py-1 rounded hover:bg-red-50"
                    onClick={handleBatchDelete}
                  >
                    <IconTrash size={16} />
                    Delete
                  </button>
                  <button
                    className="text-yellow-600 hover:text-yellow-800 flex items-center gap-1 text-sm px-3 py-1 rounded hover:bg-yellow-50"
                    onClick={handleBatchToggleStatus}
                  >
                    <IconSwitchHorizontal size={16} />
                    Toggle Status
                  </button>
                  <button
                    className="text-gray-500 hover:text-gray-700 text-sm px-3 py-1 rounded hover:bg-gray-100"
                    onClick={() => setSelectedUsers([])}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              // Default toolbar when no users are selected
              <>
                {/* SEARCH */}
                <div className="relative ml-1">
                  <input
                    type="text"
                    placeholder="Search By Name Or Email"
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="
                      border-2 border-[#463BC9] rounded text-sm px-2 py-1 pl-8 
                      focus:outline-none focus:border-[3px] 
                      peer
                    "
                  />
                  <IconSearch
                    size={16}
                    className="absolute left-2 top-2 text-gray-400 peer-focus:text-[#463BC9]"
                  />
                </div>
                {/* PAGINATION LABEL & ARROWS */}
                <span className="text-sm text-gray-700 ml-auto mr-1">
                  {fromCount} - {toCount} of {totalCount}
                </span>
                <button onClick={handlePrevPage} className="p-1 hover:bg-gray-100 rounded">
                  <IconChevronUp size={18} />
                </button>
                <button onClick={handleNextPage} className="p-1 hover:bg-gray-100 rounded">
                  <IconChevronDown size={18} />
                </button>
                {/* FILTER, REFRESH */}
                <IconButton onClick={() => setIsFilterModalOpen(true)}>
                  <IconFilter size={18} />
                </IconButton>
                <IconButton>
                  <IconRefresh size={18} />
                </IconButton>
                {/* ADD USER */}
                <button
                  className="bg-green-500 text-white text-sm px-3 py-1 rounded hover:bg-green-600 flex items-center gap-1"
                  onClick={() => navigate(`/admin/users/new`)}
                >
                  <IconCirclePlus size={18} />
                  Add User
                </button>
              </>
            )}
          </div>

          {/* TABLE */}
          <div className="overflow-x-auto h-[calc(100vh-280px)]">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-[#F9F9FF] z-20 shadow-md">
                <tr className="bg-[#F9F9FF] text-[#120E43]">
                  <th className="px-4 py-2 w-10">
                    <input
                      type="checkbox"
                      checked={selectedUsers.length > 0 && selectedUsers.length === currentUsers.length}
                      onChange={selectAllUsers}
                    />
                  </th>
                  <th className="px-4 py-2 text-left">Customer Name</th>
                  <th className="px-4 py-2 text-left">Email</th>
                  <th className="px-4 py-2 text-left">Phone Number</th>
                  <th className="px-4 py-2 text-left"># Orders</th>
                  <th className="px-4 py-2 text-left">Status</th>
                  <th className="px-4 py-2 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => {
                  const isActive = user.account_status.toString().trim().toLowerCase() === "active";
                  return (
                    <tr
                      key={user.id}
                      className="border-b border-gray-200 hover:bg-gray-50"
                    >
                      <td className="px-4 py-2">
                        <input
                          type="checkbox"
                          checked={selectedUsers.includes(user.id)}
                          onChange={() => toggleUserSelection(user.id)}
                        />
                      </td>
                      <td className="px-4 py-2">{user.full_name}</td>
                      <td className="px-4 py-2">{user.email}</td>
                      <td className="px-4 py-2">{user.phone_number || '-'}</td>
                      <td className="px-4 py-2">{user.total_order_history_count}</td>
                      <td className="px-4 py-2">
                        {isActive ? (
                          <span className="flex items-center gap-1">
                            <span role="img" aria-label="check">✅</span> {user.account_status}
                          </span>
                        ) : (
                          <span className="flex items-center gap-1">
                            <span role="img" aria-label="cross">❌</span> {user.account_status}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex items-center gap-2">
                          <button
                            className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
                            onClick={() => navigate(`/admin/users/${user.id}`)}
                          >
                            <IconPencil size={16} />
                          </button>
                          <button
                            className={
                              isActive
                                ? "text-yellow-600 hover:text-yellow-800 flex items-center gap-1"
                                : "text-green-600 hover:text-green-800 flex items-center gap-1"
                            }
                            title={isActive ? "Deactivate" : "Activate"}
                            onClick={() =>
                              openModal("TOGGLE", user, async () => {
                                try {
                                  await dispatch(toggleUserAccountsStatus([user.id])).unwrap();
                                } catch (error) {
                                  console.error("Error toggling user status:", error);
                                }
                              })
                            }
                          >
                            {isActive ? <IconLock size={16} /> : <IconLockOpen size={16} />}
                          </button>
                          <button
                            className="text-red-500 hover:text-red-700 flex items-center gap-1"
                            onClick={() =>
                              openModal("DELETE", user, () => {
                                try {
                                  dispatch(deleteCustomerAccounts([user.id]));
                                } catch (error) {
                                  console.error("Error deleting user:", error);
                                }
                              })
                            }
                          >
                            <IconTrash size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {currentUsers.length === 0 && (
                  <tr>
                    <td colSpan="8" className="text-center py-4 text-gray-500">
                      No users found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* BOTTOM SUMMARY ROW */}
          <div className="mt-4 flex items-center justify-between bg-white border-t border-gray-200 p-4 rounded-md shadow-sm">
            <div className="flex items-center gap-4 text-sm text-gray-700">
              <span className="flex items-center gap-1">
                <span role="img" aria-label="users">👥</span>
                Total Customers: <strong>{customer_accounts.total_customer}</strong>
              </span>
              <span className="flex items-center gap-1 text-green-600">
                <span role="img" aria-label="check">✅</span>
                Active: <strong>{customer_accounts.active}</strong>
              </span>
              <span className="flex items-center gap-1 text-red-600">
                <span role="img" aria-label="chart">📈</span>
                New Registrations: <strong>{customer_accounts.growth_status}</strong>
              </span>
            </div>
          </div>
        </div>
      </main>
      {/* MODALS */}
      {modalType === "TOGGLE" && selectedUser && (
        <ToggleUserModal user={selectedUser} onClose={closeModal} onConfirm={modalActionHandler} />
      )}
      {modalType === "DELETE" && selectedUser && (
        <DeleteUserModal user={selectedUser} onClose={closeModal} onConfirm={modalActionHandler} />
      )}

      <FilterModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        orderHistory={orderHistory}
        setOrderHistory={setOrderHistory}
        status={status}
        setStatus={setStatus}
        onApply={handleFilter}
      />
    </div>
  );
}

export default UserAccountManagement;